import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import {
  Button,
  ErrorBanner,
  Input,
  Select,
  Textarea,
  type MemberRow,
} from '@/features/issue'
import type { OrgProject } from '@/lib/api'

import { LogoUpload } from './LogoUpload'
import { ColorPicker, IconPicker, ProjectIconTile } from './ProjectIconPicker'

/**
 * The general half of project settings: what the project is called, who leads
 * it, and whether it is still in circulation.
 *
 * THE KEY IS READ-ONLY, and that is a decision rather than an omission. The key
 * is what every issue key is built from — TSK-14 — so changing it has to
 * rewrite or alias every existing reference in one transaction, which belongs
 * with a rename flow that owns the alias table. Showing the field greyed out
 * with the reason is more honest than hiding it and leaving people hunting.
 */
export function GeneralCard({
  project,
  members,
  canEdit,
  pending,
  error,
  saved,
  onSave,
}: {
  project: OrgProject
  members: MemberRow[]
  canEdit: boolean
  pending: boolean
  error: unknown
  saved: boolean
  onSave: (input: {
    name: string
    key: string
    description: string
    icon: string
    color: string
    lead_user_id: number | null
  }) => void
}) {
  const [name, setName] = useState(project.name)
  const [projectKey, setProjectKey] = useState(project.project_key ?? '')
  const [description, setDescription] = useState(project.description ?? '')
  const [icon, setIcon] = useState(project.icon ?? '')
  const [color, setColor] = useState(project.color_name ?? '')
  const [lead, setLead] = useState<string>(
    project.lead_user_id == null ? '' : String(project.lead_user_id),
  )

  // Re-seed from the stored record when it arrives or changes, so an edit made
  // in another tab is not silently overwritten by a stale field.
  useEffect(() => {
    setName(project.name)
    setProjectKey(project.project_key ?? '')
    setDescription(project.description ?? '')
    setIcon(project.icon ?? '')
    setColor(project.color_name ?? '')
    setLead(project.lead_user_id == null ? '' : String(project.lead_user_id))
  }, [project])

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSave({
      name: name.trim(),
      key: projectKey.trim().toUpperCase(),
      description,
      icon: icon.trim(),
      color: color.trim(),
      lead_user_id: lead === '' ? null : Number(lead),
    })
  }

  const dirty =
    name.trim() !== project.name ||
    projectKey.trim().toUpperCase() !== (project.project_key ?? '') ||
    description !== (project.description ?? '') ||
    icon.trim() !== (project.icon ?? '') ||
    color.trim() !== (project.color_name ?? '') ||
    (lead === '' ? null : Number(lead)) !== (project.lead_user_id ?? null)

  return (
    <section className="rounded-card border border-ink-200 bg-white">
      <header className="border-b border-ink-200 px-5 py-4">
        <h2 className="text-sm font-semibold tracking-tight text-ink-900">General</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          {canEdit ? 'What this project is called and who owns it.' : 'Only an admin can change these.'}
        </p>
      </header>

      <form onSubmit={submit} className="space-y-4 p-5" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-xs font-medium text-ink-600">
            <span className="mb-1 block">Name</span>
            <Input
              value={name}
              disabled={!canEdit || pending}
              maxLength={255}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className="block text-xs font-medium text-ink-600">
            <span className="mb-1 block">Key</span>
            <Input
              value={projectKey}
              disabled={!canEdit || pending}
              maxLength={10}
              onChange={(event) => setProjectKey(event.target.value.toUpperCase())}
              className="font-mono uppercase"
            />
            <span className="mt-1 block font-normal text-ink-500">
              Every issue key is built from it — {projectKey || 'TSK'}-14. Renaming keeps the old key
              working: it is retired rather than freed, so links already using it still resolve and
              nothing else can take it.
            </span>
          </label>
        </div>

        <label className="block text-xs font-medium text-ink-600">
          <span className="mb-1 block">Description</span>
          <Textarea
            rows={3}
            value={description}
            disabled={!canEdit || pending}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <ProjectIconTile icon={icon} color={color} />
            <div>
              <p className="text-xs font-medium text-ink-600">Appearance</p>
              <p className="text-xs text-ink-500">How this project looks in the sidebar and on iPad.</p>
            </div>
          </div>

          <div className="text-xs font-medium text-ink-600">
            <span className="mb-1 block">Icon</span>
            <IconPicker
              value={icon}
              color={color}
              disabled={!canEdit || pending}
              onChange={setIcon}
            />
          </div>

          <div className="text-xs font-medium text-ink-600">
            <span className="mb-1 block">Colour</span>
            <ColorPicker value={color} disabled={!canEdit || pending} onChange={setColor} />
          </div>
        </div>

        {/* Outside the save form on purpose: an upload is its own request that
            takes effect immediately, so putting it behind "Save changes" would
            imply the file is still pending when it is already stored. */}
        <LogoUpload
          projectKey={project.project_key ?? project.id}
          icon={icon}
          color={color}
          hasLogo={project.has_logo === true}
          canEdit={canEdit}
        />

        <div className="grid gap-4 sm:grid-cols-3">

          <label className="block text-xs font-medium text-ink-600">
            <span className="mb-1 block">Lead</span>
            <Select
              value={lead}
              disabled={!canEdit || pending}
              onChange={(event) => setLead(event.target.value)}
            >
              <option value="">Nobody</option>
              {members.map((member) => (
                <option key={member.user_id} value={String(member.user_id)}>
                  {member.user?.name ?? `User ${member.user_id}`}
                </option>
              ))}
            </Select>
          </label>
        </div>

        {error ? <ErrorBanner error={error} /> : null}
        {saved && !dirty ? (
          <p role="status" className="text-sm text-emerald-700">
            Project updated.
          </p>
        ) : null}

        {canEdit ? (
          <Button type="submit" variant="primary" disabled={!dirty || !name.trim() || pending}>
            {pending ? 'Saving…' : 'Save changes'}
          </Button>
        ) : null}
      </form>
    </section>
  )
}

/**
 * The colour names the iPad app knows. They are NAMES, not CSS colours — the
 * column stores one of these and each client maps it to its own palette — so
 * the field is a picker rather than free text.
 */
